import { supabase } from '../lib/supabase';
import { WinnerEntry, Event } from '../types/models/event';

// Re-export types for use in components
export type { Event, EventRequirements, EventMode } from '../types/models/event';
export type { WinnerEntry, LeaderboardEntry } from '../types/models/event';

export interface EventSubmission {
  id: string;
  eventId: string;
  userId: string;
  /** Internal Supabase DB id — used for follow-count enrichment, not displayed */
  _internalId?: string;
  userName: string;
  userAvatar?: string;
  email?: string;
  role?: string;
  bio?: string;
  location?: string;
  city?: string;
  state?: string;
  country?: string;
  mobile?: string;
  phone?: string;
  sports?: string[];
  eventTypes?: string[];
  positionName?: string;
  subcategoryName?: string;
  followersCount?: number;
  followingCount?: number;
  isVerified?: boolean;
  isActive?: boolean;
  videoUrl: string;
  thumbnail?: string;
  title: string;
  description?: string;
  status: 'draft' | 'submitted';
  rank?: number;
  uploadedAt?: any;
  updatedAt?: any;
  isParticipant?: boolean; // true for offline event entries (no video)
}

class EventsService {
  private normalizeSports(value: any): string[] {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value
        .map((entry) => {
          if (typeof entry === 'string') return entry.trim();
          if (entry && typeof entry === 'object') return String(entry.name || entry.title || entry.id || '').trim();
          return '';
        })
        .filter(Boolean);
    }
    if (typeof value === 'string') {
      return value
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean);
    }
    return [];
  }

  private mapUserDetails(user: any): Partial<EventSubmission> {
    if (!user) return {};
    const city = user.city ? String(user.city).trim() : '';
    const state = user.state ? String(user.state).trim() : '';
    const country = user.country ? String(user.country).trim() : '';
    const fallbackLocation = [city, state, country].filter(Boolean).join(', ');

    // Resolve sports from all known field variants (mirrors userManagementService)
    const sportsCandidates = [
      user.sports,
      user.sport,
      user.sport_details,
      user.favorite_sports,
      user.athlete_profile?.sports,
      user.athlete_profile?.sport
    ];
    const allSports = sportsCandidates.flatMap((v) => this.normalizeSports(v));
    const uniqueSports = Array.from(new Set(allSports.map((s) => s.toLowerCase())))
      .map((lower) => allSports.find((s) => s.toLowerCase() === lower) as string);

    // Parse follower/following from denormalized columns; actual live counts are
    // enriched later via the `follows` table in getEventParticipantDetails.
    const parseCount = (v: any) => {
      const n = Number(v);
      return Number.isFinite(n) && n >= 0 ? n : undefined;
    };

    return {
      email: user.email || undefined,
      role: user.role || undefined,
      bio: user.bio || undefined,
      location: user.location || fallbackLocation || undefined,
      city: user.city || undefined,
      state: user.state || undefined,
      country: user.country || undefined,
      mobile: user.mobile || user.mobile_number || undefined,
      phone: user.phone || user.phone_number || undefined,
      sports: uniqueSports.length > 0 ? uniqueSports : undefined,
      eventTypes: this.normalizeSports(user.event_types),
      positionName: user.position_name || user.position || undefined,
      subcategoryName: user.subcategory_name || user.subcategory || undefined,
      followersCount: parseCount(user.followers_count),
      followingCount: parseCount(user.following_count),
      isVerified: typeof user.is_verified === 'boolean' ? user.is_verified : undefined,
      isActive: typeof user.is_active === 'boolean' ? user.is_active : undefined
    };
  }

  /** Fetch accurate follower/following counts from the `follows` table for a set of internal user IDs. */
  private async fetchFollowCounts(internalIds: string[]): Promise<{
    followersByUser: Map<string, number>;
    followingByUser: Map<string, number>;
  }> {
    const followersByUser = new Map<string, number>();
    const followingByUser = new Map<string, number>();
    if (internalIds.length === 0) return { followersByUser, followingByUser };

    const [{ data: followerRows }, { data: followingRows }] = await Promise.all([
      supabase.from('follows').select('following_id').in('following_id', internalIds),
      supabase.from('follows').select('follower_id').in('follower_id', internalIds)
    ]);

    (followerRows || []).forEach((row: any) => {
      const key = row?.following_id;
      if (key) followersByUser.set(key, (followersByUser.get(key) || 0) + 1);
    });
    (followingRows || []).forEach((row: any) => {
      const key = row?.follower_id;
      if (key) followingByUser.set(key, (followingByUser.get(key) || 0) + 1);
    });

    return { followersByUser, followingByUser };
  }

  private async enrichEventsWithWinnerLeaderboard(rows: any[]): Promise<any[]> {
    if (!Array.isArray(rows) || rows.length === 0) return rows;

    const eventIds = rows
      .map((row) => row?.id)
      .filter((id): id is string => typeof id === 'string' && id.length > 0);

    if (eventIds.length === 0) return rows;

    const { data: winnerRows, error: winnerError } = await supabase
      .from('event_winners')
      .select('event_id, user_id, rank, prize, submission_id')
      .in('event_id', eventIds);

    if (winnerError || !winnerRows) {
      if (winnerError) {
        console.warn('Unable to fetch event_winners leaderboard data:', winnerError);
      }
      return rows;
    }

    const userIds = Array.from(new Set(
      winnerRows
        .map((winner: any) => winner?.user_id)
        .filter((id: any): id is string => typeof id === 'string' && id.length > 0)
    ));

    let userById = new Map<string, any>();
    if (userIds.length > 0) {
      const { data: users } = await supabase
        .from('users')
        .select('id, uid, display_name, photo_url')
        .in('id', userIds);
      userById = new Map((users || []).map((user: any) => [user.id, user]));
    }

    const leaderboardByEvent = new Map<string, any[]>();
    winnerRows.forEach((winner: any) => {
      const eventId = winner.event_id;
      if (!eventId) return;

      const user = userById.get(winner.user_id);
      const entry = {
        rank: Number(winner.rank) || 0,
        userId: user?.uid || winner.user_id,
        userName: user?.display_name || user?.uid || winner.user_id,
        userAvatar: user?.photo_url || undefined,
        prize: winner.prize || undefined,
        submissionId: winner.submission_id || ''
      };

      const existing = leaderboardByEvent.get(eventId) || [];
      existing.push(entry);
      leaderboardByEvent.set(eventId, existing);
    });

    leaderboardByEvent.forEach((entries, eventId) => {
      entries.sort((a, b) => a.rank - b.rank);
      leaderboardByEvent.set(eventId, entries);
    });

    return rows.map((row) => ({
      ...row,
      leaderboard: leaderboardByEvent.get(row.id) || []
    }));
  }


  /**
   * Strip empty/default values from metadata so non-tournament events
   * don't get noise like {sport:'', registrationFee:0} in the JSONB column.
   */
  private cleanMetadata(metadata: any): any {
    if (!metadata || typeof metadata !== 'object') return {};
    const cleaned: any = {};
    for (const [key, value] of Object.entries(metadata)) {
      if (value === '' || value === undefined || value === null) continue;
      if (value === 0 && key !== 'winnerCount') continue; // 0 is a default, skip
      cleaned[key] = value;
    }
    return cleaned;
  }

  private cleanLocalizedTextMap(value: any): Record<string, string> | undefined {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
    const cleaned: Record<string, string> = {};
    for (const [rawLang, rawText] of Object.entries(value)) {
      if (typeof rawText !== 'string') continue;
      const lang = String(rawLang || '').trim().toLowerCase();
      const text = rawText.trim();
      if (!lang || !text) continue;
      cleaned[lang] = text;
    }
    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
  }

  private cleanLocalizedCriteriaMap(value: any): Record<string, string[]> | undefined {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
    const cleaned: Record<string, string[]> = {};
    for (const [rawLang, rawCriteria] of Object.entries(value)) {
      if (!Array.isArray(rawCriteria)) continue;
      const lang = String(rawLang || '').trim().toLowerCase();
      const criteria = rawCriteria
        .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
        .filter(Boolean);
      if (!lang || criteria.length === 0) continue;
      cleaned[lang] = criteria;
    }
    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
  }

  private cleanEventRequirements(requirements: any): any {
    if (!requirements || typeof requirements !== 'object') return {};

    const description = typeof requirements.description === 'string'
      ? requirements.description.trim()
      : '';

    const criteria = Array.isArray(requirements.criteria)
      ? requirements.criteria
          .map((entry: any) => (typeof entry === 'string' ? entry.trim() : ''))
          .filter(Boolean)
      : [];

    const descriptionByLanguage = this.cleanLocalizedTextMap(
      requirements.descriptionByLanguage
    );
    const criteriaByLanguage = this.cleanLocalizedCriteriaMap(
      requirements.criteriaByLanguage
    );

    const cleaned: any = {};
    if (description) cleaned.description = description;
    if (criteria.length > 0) cleaned.criteria = criteria;
    if (descriptionByLanguage) cleaned.descriptionByLanguage = descriptionByLanguage;
    if (criteriaByLanguage) cleaned.criteriaByLanguage = criteriaByLanguage;

    return cleaned;
  }

  // Create new event
  async createEvent(eventData: Omit<Event, 'id' | 'createdAt' | 'updatedAt' | 'participants'>): Promise<string> {
    try {
      // Combine separate date + time into single ISO datetime for the date column
      // This prevents timezone bugs where date-only strings are parsed as UTC midnight
      const combinedDate = this.combineDateAndTime(
        typeof eventData.date === 'string' ? eventData.date : eventData.date?.toString(),
        eventData.time
      );

      // --- description column: JSONB with all language descriptions ---
      const descriptionJson: Record<string, string> = {
        ...((eventData as any).descriptionByLanguage || {})
      };
      if (eventData.description?.trim() && !descriptionJson['en']) {
        descriptionJson['en'] = eventData.description.trim();
      }

      // --- requirements column: JSONB with criteria per language ---
      const criteriaPayload = this.cleanLocalizedCriteriaMap(
        (eventData as any).eventRequirements?.criteriaByLanguage
      ) || null;

      // --- event_requirements column: multilingual rules text + metadata + prize ---
      const rulesDescByLang = this.cleanLocalizedTextMap(
        (eventData as any).eventRequirements?.descriptionByLanguage
      );
      const mergedRequirements: any = {
        ...this.cleanMetadata(eventData.metadata),
        ...((eventData as any).prizeDescription ? { prizeDescription: (eventData as any).prizeDescription } : {}),
        ...(rulesDescByLang || {})
      };

      // Map to Supabase column names (snake_case)
      const dbPayload: any = {
        title: eventData.title,
        description: Object.keys(descriptionJson).length > 0 ? descriptionJson : (eventData.description || null),
        requirements: criteriaPayload,
        date: combinedDate,
        time: eventData.time,
        location: eventData.location,
        image_url: eventData.imageUrl,
        category: eventData.category,
        max_participants: eventData.maxParticipants,
        winner_count: eventData.winnerCount,
        submission_deadline: eventData.submissionDeadline,
        event_requirements: Object.keys(mergedRequirements).length > 0 ? mergedRequirements : null,
        event_mode: eventData.eventMode ?? 'online',
        is_active: eventData.isActive ?? true,
        status: eventData.status ?? 'upcoming',
        organizer: eventData.organizer || 'Admin',
        contact_email: eventData.contactEmail || 'admin@amaplayer.com',
        prizes: (eventData as any).prizeImages?.length > 0 ? (eventData as any).prizeImages : null,
        end_date: (eventData as any).endDate || null,
        end_time: (eventData as any).endTime || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Remove undefined values
      Object.keys(dbPayload).forEach(key => {
        if (dbPayload[key] === undefined) {
          delete dbPayload[key];
        }
      });

      const { data, error } = await supabase
        .from('events')
        .insert(dbPayload)
        .select('id')
        .single();

      if (error) throw error;

      console.log('Event created successfully with ID:', data.id);
      return data.id;
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  }

  // Get all events
  async getAllEvents(): Promise<Event[]> {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*, event_participations(user_id), event_submissions(user_id)')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const enriched = await this.enrichEventsWithWinnerLeaderboard(data || []);
      return enriched.map((row: any) => this.mapToModel(row));
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  }

  // Get active events
  async getActiveEvents(): Promise<Event[]> {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('is_active', true)
        .order('date', { ascending: true });

      if (error) throw error;

      const enriched = await this.enrichEventsWithWinnerLeaderboard(data || []);
      return enriched.map((row: any) => this.mapToModel(row));
    } catch (error) {
      console.error('Error fetching active events:', error);
      throw error;
    }
  }

  // Update event
  async updateEvent(eventId: string, updates: Partial<Event>): Promise<void> {
    try {
      // Map camelCase to snake_case for Supabase
      const dbUpdates: any = { updated_at: new Date().toISOString() };

      if (updates.title !== undefined) dbUpdates.title = updates.title;

      // description column: JSONB with all language descriptions
      if (updates.description !== undefined || (updates as any).descriptionByLanguage !== undefined) {
        const descJson: Record<string, string> = { ...((updates as any).descriptionByLanguage || {}) };
        if (updates.description?.trim() && !descJson['en']) descJson['en'] = updates.description.trim();
        dbUpdates.description = Object.keys(descJson).length > 0 ? descJson : (updates.description || null);
      }

      // Write both date + time together when either changes to keep them in sync
      if (updates.date !== undefined || updates.time !== undefined) {
        const dateStr = typeof updates.date === 'string' ? updates.date : updates.date?.toString();
        dbUpdates.date = this.combineDateAndTime(dateStr, updates.time);
        if (updates.time !== undefined) dbUpdates.time = updates.time;
      }
      if (updates.location !== undefined) dbUpdates.location = updates.location;
      if (updates.category !== undefined) dbUpdates.category = updates.category;
      if (updates.imageUrl !== undefined) dbUpdates.image_url = updates.imageUrl;
      if (updates.maxParticipants !== undefined) {
        const parsedMax = Number(updates.maxParticipants);
        dbUpdates.max_participants = Number.isFinite(parsedMax) ? parsedMax : updates.maxParticipants;
      }
      if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.eventMode !== undefined) dbUpdates.event_mode = updates.eventMode;
      if (updates.winnerCount !== undefined) dbUpdates.winner_count = updates.winnerCount;
      if (updates.submissionDeadline !== undefined) dbUpdates.submission_deadline = updates.submissionDeadline;

      // requirements column: JSONB criteria per language
      if (updates.eventRequirements !== undefined) {
        dbUpdates.requirements = this.cleanLocalizedCriteriaMap(
          updates.eventRequirements?.criteriaByLanguage
        ) || null;
      }

      // event_requirements column: multilingual rules text + metadata + prize
      if (updates.eventRequirements !== undefined || updates.metadata !== undefined || (updates as any).prizeDescription !== undefined) {
        const rulesDescByLang = this.cleanLocalizedTextMap(updates.eventRequirements?.descriptionByLanguage);
        const merged: any = {
          ...this.cleanMetadata(updates.metadata),
          ...((updates as any).prizeDescription ? { prizeDescription: (updates as any).prizeDescription } : {}),
          ...(rulesDescByLang || {})
        };
        dbUpdates.event_requirements = Object.keys(merged).length > 0 ? merged : null;
      }

      if ((updates as any).prizeImages !== undefined) {
        dbUpdates.prizes = (updates as any).prizeImages?.length > 0 ? (updates as any).prizeImages : null;
      }
      if ((updates as any).endDate !== undefined) dbUpdates.end_date = (updates as any).endDate || null;
      if ((updates as any).endTime !== undefined) dbUpdates.end_time = (updates as any).endTime || null;

      const { error } = await supabase
        .from('events')
        .update(dbUpdates)
        .eq('id', eventId);

      if (error) throw error;
      console.log('Event updated successfully:', eventId);
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  }

  // Delete event
  async deleteEvent(eventId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', eventId);

      if (error) throw error;
      console.log('Event deleted successfully:', eventId);
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  }

  // Toggle event active status
  async toggleEventStatus(eventId: string, isActive: boolean): Promise<void> {
    try {
      const { error } = await supabase
        .from('events')
        .update({ is_active: isActive })
        .eq('id', eventId);

      if (error) throw error;
    } catch (error) {
      console.error('Error toggling event status:', error);
      throw error;
    }
  }

  // Bulk activate events
  async bulkActivateEvents(eventIds: string[], reason?: string): Promise<any> {
    const result = { processedCount: 0, failedCount: 0, errors: [] as any[] };
    try {
      const { error } = await supabase
        .from('events')
        .update({ is_active: true, activation_reason: reason })
        .in('id', eventIds);

      if (error) throw error;
      result.processedCount = eventIds.length;
    } catch (e: any) {
      result.failedCount = eventIds.length;
      result.errors.push({ eventId: 'batch', error: e.message });
    }
    return result;
  }

  // Bulk deactivate events
  async bulkDeactivateEvents(eventIds: string[], reason?: string): Promise<any> {
    const result = { processedCount: 0, failedCount: 0, errors: [] as any[] };
    try {
      const { error } = await supabase
        .from('events')
        .update({ is_active: false, deactivation_reason: reason })
        .in('id', eventIds);

      if (error) throw error;
      result.processedCount = eventIds.length;
    } catch (e: any) {
      result.failedCount = eventIds.length;
      result.errors.push({ eventId: 'batch', error: e.message });
    }
    return result;
  }

  // Get all submissions for an online event (video required)
  async getEventSubmissions(eventId: string): Promise<EventSubmission[]> {
    try {
      const { data, error } = await supabase
        .from('event_submissions')
        .select(`
          *,
          user:users!user_id(*)
        `)
        .eq('event_id', eventId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data.map(sub => ({
        id: sub.id,
        eventId: sub.event_id,
        userId: sub.user?.uid || sub.user_id,
        _internalId: sub.user?.id || sub.user_id,
        userName: sub.user?.display_name || sub.user?.email || 'Unknown User',
        userAvatar: sub.user?.photo_url,
        ...this.mapUserDetails(sub.user),
        videoUrl: sub.video_url,
        thumbnail: sub.thumbnail_url,
        title: sub.title,
        description: sub.description,
        status: sub.status,
        rank: sub.rank,
        uploadedAt: sub.created_at,
        updatedAt: sub.updated_at,
        isParticipant: false
      }));
    } catch (error) {
      console.error('Error fetching event submissions:', error);
      return [];
    }
  }

  // Get all participants for an offline event (joining counts as entry)
  async getEventParticipants(eventId: string): Promise<EventSubmission[]> {
    try {
      const { data, error } = await supabase
        .from('event_participations')
        .select(`
          *,
          user:users!user_id(*)
        `)
        .eq('event_id', eventId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data.map(p => ({
        id: p.id,
        eventId: eventId,
        userId: p.user?.uid || p.user_id,
        _internalId: p.user?.id || p.user_id,
        userName: p.user?.display_name || p.user?.email || 'Unknown Participant',
        userAvatar: p.user?.photo_url,
        ...this.mapUserDetails(p.user),
        videoUrl: '',
        title: p.user?.display_name || 'Participant',
        description: 'Offline event participant',
        status: 'submitted' as const,
        uploadedAt: p.created_at,
        isParticipant: true
      }));
    } catch (error) {
      console.error('Error fetching event participants:', error);
      return [];
    }
  }

  // Get event with submissions
  async getEventWithSubmissions(eventId: string): Promise<{ event: Event | null; submissions: EventSubmission[] }> {
    try {
      const { data: eventData, error: eventError } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .single();

      if (eventError || !eventData) return { event: null, submissions: [] };

      const [enrichedEvent] = await this.enrichEventsWithWinnerLeaderboard([eventData]);
      const event = this.mapToModel(enrichedEvent);
      const submissions = await this.getEventSubmissions(eventId);

      return { event, submissions };
    } catch (error) {
      console.error('Error fetching event with submissions:', error);
      return { event: null, submissions: [] };
    }
  }

  // Declare winners using event_winners table as source of truth.
  async declareWinners(eventId: string, winners: WinnerEntry[], adminId: string): Promise<Event | null> {
    try {
      const uids = winners.map((winner) => winner.userId);
      const [{ data: usersByUid }, { data: usersById }] = await Promise.all([
        supabase
          .from('users')
          .select('id, uid, display_name, photo_url')
          .in('uid', uids),
        supabase
          .from('users')
          .select('id, uid, display_name, photo_url')
          .in('id', uids)
      ]);

      const resolvedUsers = [...(usersByUid || []), ...(usersById || [])];
      const userByUid = new Map(resolvedUsers.map((user) => [user.uid, user]));
      const userById = new Map(resolvedUsers.map((user) => [user.id, user]));

      const winnerRows = winners
        .map((winner) => {
          const user = userByUid.get(winner.userId) || userById.get(winner.userId);
          return {
            event_id: eventId,
            user_id: user?.id || null,
            rank: winner.rank,
            prize: (winner as any).prize ?? null,
            submission_id: winner.submissionId || null
          };
        })
        .filter((row) => Boolean(row.user_id));

      const { error: deleteWinnersError } = await supabase
        .from('event_winners')
        .delete()
        .eq('event_id', eventId);
      if (deleteWinnersError) throw deleteWinnersError;

      if (winnerRows.length > 0) {
        const { error: insertWinnersError } = await supabase
          .from('event_winners')
          .insert(winnerRows);
        if (insertWinnersError) throw insertWinnersError;
      }

      const { error: eventUpdateError } = await supabase
        .from('events')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', eventId);
      if (eventUpdateError) {
        console.warn('Event timestamp update skipped:', eventUpdateError);
      }

      const { data: updated, error } = await supabase
        .from('events')
        .select('*, event_participations(user_id), event_submissions(user_id)')
        .eq('id', eventId)
        .single();

      if (error) throw error;

      const [enrichedEvent] = await this.enrichEventsWithWinnerLeaderboard([updated]);
      return this.mapToModel(enrichedEvent);
    } catch (error) {
      console.error('Error declaring winners:', error);
      throw error;
    }
  }

  // Get complete participant details with uploaded video mapped per user
  async getEventParticipantDetails(eventId: string): Promise<EventSubmission[]> {
    try {
      const [participants, submissions] = await Promise.all([
        this.getEventParticipants(eventId),
        this.getEventSubmissions(eventId)
      ]);

      const byUser = new Map<string, EventSubmission>();

      participants.forEach((participant) => {
        byUser.set(participant.userId, participant);
      });

      submissions.forEach((submission) => {
        const existing = byUser.get(submission.userId);
        if (!existing) {
          byUser.set(submission.userId, submission);
          return;
        }

        // Preserve full participant profile details, but attach latest uploaded media.
        if (!existing.videoUrl && submission.videoUrl) {
          byUser.set(submission.userId, {
            ...existing,
            videoUrl: submission.videoUrl,
            thumbnail: submission.thumbnail,
            title: submission.title || existing.title,
            description: submission.description || existing.description,
            status: submission.status || existing.status,
            rank: submission.rank ?? existing.rank,
            uploadedAt: submission.uploadedAt || existing.uploadedAt,
            updatedAt: submission.updatedAt || existing.updatedAt
          });
        }
      });

      const merged = Array.from(byUser.values());

      // Enrich with accurate follower/following counts from the `follows` table.
      // The denormalized columns on `users` can be stale; the relationship table is authoritative.
      const internalIds = merged
        .map((e) => e._internalId)
        .filter((id): id is string => Boolean(id));

      if (internalIds.length > 0) {
        const { followersByUser, followingByUser } = await this.fetchFollowCounts(internalIds);
        merged.forEach((entry) => {
          if (!entry._internalId) return;
          const liveFollowers = followersByUser.get(entry._internalId);
          const liveFollowing = followingByUser.get(entry._internalId);
          if (liveFollowers !== undefined) entry.followersCount = liveFollowers;
          if (liveFollowing !== undefined) entry.followingCount = liveFollowing;
        });
      }

      return merged.sort((a, b) => (a.userName || '').localeCompare(b.userName || ''));
    } catch (error) {
      console.error('Error fetching event participant details:', error);
      return [];
    }
  }


  /**
   * Combine separate date + time columns into a single ISO datetime string.
   * Protects against legacy date-only rows being parsed as UTC midnight.
   */
  private combineDateAndTime(date: string | undefined, time: string | undefined): string {
    if (!date) return new Date().toISOString();
    if (date.includes('T')) return date;
    if (time) return `${date}T${time}:00`;
    return `${date}T12:00:00`;
  }

  /**
   * Extract HH:mm time string from an ISO datetime, falling back to the
   * separate `time` column for legacy rows that store date-only values.
   */
  private extractTime(dateStr: string | undefined, fallbackTime: string | undefined): string {
    // Prefer explicit `time` column when present to avoid timezone side-effects
    // from parsing the `date` timestamp.
    if (fallbackTime) {
      return fallbackTime.substring(0, 5);
    }

    if (dateStr && dateStr.includes('T')) {
      const timePart = dateStr.split('T')[1]; // "16:00:00" or "16:00:00+00:00"
      return timePart ? timePart.substring(0, 5) : fallbackTime || '';
    }
    return fallbackTime || '';
  }

  /**
   * Auto-compute event status from date and leaderboard.
   *   - Has winners / leaderboard → 'completed'
   *   - Event date is in the past  → 'completed'
   *   - Otherwise                  → 'upcoming'
   */
  private parseEventDateTime(dateValue: string | undefined, timeValue: string | undefined): Date | null {
    if (!dateValue) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      const normalizedTime = timeValue && /^\d{2}:\d{2}/.test(timeValue) ? `${timeValue}:00` : '23:59:59';
      const parsed = new Date(`${dateValue}T${normalizedTime}`);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    const parsed = new Date(dateValue);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private parseDateValue(value: any): Date | null {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private computeStatus(dateStr: string | undefined, timeStr: string | undefined, leaderboard: any[] | undefined): 'upcoming' | 'completed' {
    if (leaderboard && leaderboard.length > 0) return 'completed';
    return 'upcoming';
  }

  private mapToModel(data: any): Event {
    // Trust the `date` column as source of truth (stores full ISO datetime)
    const dateValue = data.date || new Date().toISOString();
    const maxParticipantsRaw = data.max_participants ?? data.maxParticipants;
    const maxParticipantsParsed = Number(maxParticipantsRaw);
    const eventDateTime = this.parseEventDateTime(data.date, data.time);
    const submissionDeadlineDate = this.parseDateValue(data.submission_deadline);
    const expirationReferenceDate = submissionDeadlineDate || eventDateTime;
    const isExpired = expirationReferenceDate ? expirationReferenceDate.getTime() < Date.now() : false;

    // --- description column: JSONB object, JSON string, or legacy plain text ---
    const rawDesc = data.description;
    let primaryDescription = '';
    let descriptionByLanguage: Record<string, string> | undefined;

    // Normalise: if Supabase returned a JSON string (text column) try to parse it
    let parsedDesc: any = rawDesc;
    if (typeof rawDesc === 'string') {
      const trimmed = rawDesc.trim();
      if (trimmed.startsWith('{')) {
        try { parsedDesc = JSON.parse(trimmed); } catch { /* keep as string */ }
      }
    }

    if (typeof parsedDesc === 'string') {
      primaryDescription = parsedDesc;
    } else if (parsedDesc && typeof parsedDesc === 'object') {
      const cleaned = this.cleanLocalizedTextMap(parsedDesc);
      if (cleaned) {
        descriptionByLanguage = cleaned;
        primaryDescription = cleaned['en'] || cleaned['hi'] || Object.values(cleaned)[0] || '';
      }
    }

    // --- requirements column: criteria per language (JSONB or JSON string) ---
    let parsedRequirements: any = data.requirements;
    if (typeof parsedRequirements === 'string') {
      const trimmed = parsedRequirements.trim();
      if (trimmed.startsWith('{')) {
        try { parsedRequirements = JSON.parse(trimmed); } catch { parsedRequirements = null; }
      } else {
        parsedRequirements = null;
      }
    }
    const criteriaByLanguage = this.cleanLocalizedCriteriaMap(parsedRequirements) || undefined;
    const primaryCriteria: string[] = criteriaByLanguage
      ? (criteriaByLanguage['en'] || criteriaByLanguage['hi'] || Object.values(criteriaByLanguage)[0] || [])
      : [];

    // --- event_requirements column: multilingual rules + metadata ---
    // Language-keyed entries (2-letter codes) = rules description by language
    // Other entries = metadata (sport, tournamentType, registrationFee, prizeDescription, etc.)
    const LANG_CODES = new Set(['en', 'hi', 'mr', 'ta', 'te', 'bn', 'gu', 'pa', 'kn', 'ml']);
    let parsedEventReq: any = data.event_requirements;
    if (typeof parsedEventReq === 'string') {
      const trimmed = parsedEventReq.trim();
      if (trimmed.startsWith('{')) {
        try { parsedEventReq = JSON.parse(trimmed); } catch { parsedEventReq = null; }
      } else {
        parsedEventReq = null;
      }
    }
    const rawReq = parsedEventReq && typeof parsedEventReq === 'object' ? parsedEventReq : null;
    const rulesRaw: Record<string, string> = {};
    const metadataObj: Record<string, any> = {};
    if (rawReq) {
      for (const [key, val] of Object.entries(rawReq)) {
        if (LANG_CODES.has(key) && typeof val === 'string') {
          rulesRaw[key] = val as string;
        } else {
          metadataObj[key] = val;
        }
      }
    }
    const rulesDescByLanguage = this.cleanLocalizedTextMap(rulesRaw) || undefined;
    const primaryRulesDesc = rulesDescByLanguage
      ? (rulesDescByLanguage['en'] || rulesDescByLanguage['hi'] || Object.values(rulesDescByLanguage)[0] || '')
      : '';

    const hasRequirementContent = Boolean(
      primaryRulesDesc || primaryCriteria.length > 0 || rulesDescByLanguage || criteriaByLanguage
    );

    return {
      id: data.id,
      title: data.title,
      description: primaryDescription,
      descriptionByLanguage,
      date: dateValue,
      time: this.extractTime(data.date, data.time),
      location: data.location,
      imageUrl: data.image_url,
      category: data.category,
      maxParticipants: Number.isFinite(maxParticipantsParsed) ? maxParticipantsParsed : undefined,
      status: this.computeStatus(data.date, data.time, data.leaderboard),
      isActive: Boolean(data.is_active) && !isExpired,
      eventMode: data.event_mode ?? 'online',
      winnerCount: data.winner_count,
      submissionDeadline: data.submission_deadline,
      eventRequirements: hasRequirementContent ? {
        description: primaryRulesDesc,
        criteria: primaryCriteria.length > 0 ? primaryCriteria : undefined,
        ...(rulesDescByLanguage ? { descriptionByLanguage: rulesDescByLanguage } : {}),
        ...(criteriaByLanguage ? { criteriaByLanguage } : {})
      } : undefined,
      metadata: metadataObj,
      leaderboard: data.leaderboard,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      // Entrants come from two tables: joining (event_participations) and
      // uploading a video (event_submissions). Online events are usually entered
      // by submitting alone, so counting participations only under-reports them.
      // Dedup because a user can both join and submit, and can join more than once.
      participants: Array.from(new Set([
        ...(data.event_participations || []).map((p: any) => p.user_id),
        ...(data.event_submissions || []).map((s: any) => s.user_id)
      ].filter(Boolean))),
      organizer: data.organizer,
      contactEmail: data.contact_email,
      prizeImages: Array.isArray(data.prizes) && data.prizes.length > 0 ? data.prizes : undefined,
      prizeDescription: metadataObj?.prizeDescription || undefined,
      endDate: data.end_date || undefined,
      endTime: data.end_time || undefined
    } as Event;
  }
}

export const eventsService = new EventsService();
export default eventsService; // Default export for compatibility

